import React from "react";
import { useMediaQuery } from "@chakra-ui/react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Flex,
  LinkBox, 
  LinkOverlay,
} from "@chakra-ui/react";
import "./mobile-panel.scss";
import { Logo } from "../../assets/react/Logo";
import moment from 'moment-timezone';

type MobilePanelType = {
  isMobilePanelOpen: boolean;
  onMobilePanelOpen: () => void;
  onMobilePanelClose: () => void
}

//  The mobile navbar side panel triggered by the hamburger button 

const MobilePanel = ({ isMobilePanelOpen, onMobilePanelOpen, onMobilePanelClose }: MobilePanelType) => {
  // breakpoints
  const [ isLargerThan576 ] = useMediaQuery("(min-width: 576px)");

  // Show ticket modal between June 14,2023 and Sep 18,2023
  const currentTime = moment().tz('America/Toronto');
  const showTicketModal = moment(currentTime).isAfter('2024-06-19T10:00', 'minute') && moment(currentTime).isBefore('2024-09-16T00:00', 'minute');

  // show tiff market link after May 16,2025 9am
  const showTiffMarketLink = moment(currentTime).isAfter('2025-05-16T08:55', 'minute');

  return (
    <>
      <Drawer
        isOpen={ isMobilePanelOpen }
        onClose={ onMobilePanelClose }
        placement="top"
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>

          {/* top section */}
          <DrawerHeader background='black' padding={{base: '16px 22px 8px 22px',  sm: '32px 45px 16px 45px'}} marginBottom="-2px">
            <Box 
              className="mobilePanel-top-section"
              display="inline-flex" 
              w="100%" 
              justifyContent="center" 
              alignItems="center"
            >
              <LinkBox>
                <LinkOverlay className="mobile-nav-tiff-logo" href="/">
                  <Logo />
                </LinkOverlay>
              </LinkBox>
              <Flex
                position="absolute"
                top={{base:"18px", sm:"35px"}}
                right={{base:"23px",  sm:"45px"}}
                alignItems="center"
                justify="center"
              >

                <DrawerCloseButton fontSize={{base:'12px', sm: '26px' }}  
                  className="mobile-nav-header-icon"
                  position='static'
                  outline="0 !important"
                  border="0 !important"
                  marginBottom="0px"
                  marginRight="0px"
                />

              </Flex>

            </Box>
          </DrawerHeader>
          
          {/* Accordian menu */}
          <DrawerBody background='black' padding={{base:"35px 60px 90px 60px", sm:"70px 120px 100px 120px"}}>

            <Accordion variant="primary" allowToggle>
              {/* section 1 */}
              <AccordionItem marginBottom={{base:'8px', sm: '30px'}}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton marginBottom="0px" outline="0 !important" 
                    border="0 !important" padding="8px 0px 0px 0px !important"
                  >
                   <Box as="span" flex="1"
                      fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700" 
                      textAlign="left" maxWidth="fit-content !important"
                      paddingBottom={{base:"5px", sm:"9px"}} fontSize={{base: "16px", sm: "28px"}} lineHeight={{base: "16px", sm: "28px"}}
                      borderBottom={isExpanded ? {base:"4px solid #fff", sm:"8px solid #fff"} : "none"}
                    >
                      About Us
                    </Box>
                    <AccordionIcon width={{base: "28px", sm: "50px"}}
                      height={{base: "28px", sm: "50px"}}
                      marginBottom="8px"
                    />
                  </AccordionButton>
                <AccordionPanel padding="0px !important">

                  <Accordion variant="secondary" allowToggle>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                      >
                        <Box 
                          as="span" 
                          flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" 
                          fontSize={{base: "14px", sm: "24px"}} 
                          lineHeight={{base: "14px", sm: "24px"}}
                        >
                          Who We Are
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                        
                        <LinkBox 
                          fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif"
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/mission">
                            Our Mission
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox 
                          fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif"
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/land-acknowledgement">
                            Land Acknowledgement
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/about">
                            TIFF Staff & Board 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/code-of-conduct">
                            Code of Conduct 
                          </LinkOverlay>
                        </LinkBox>

                        {/* <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/land-acknowledgement">
                            Reconcilliation
                          </LinkOverlay>
                        </LinkBox> */}

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                          <LinkOverlay href="/partnerships">
                            Partnerships 
                          </LinkOverlay>
                        </LinkBox>

                      </AccordionPanel>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                      >
                        <Box 
                          as="span" 
                          flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" 
                          fontSize={{ base: "14px", sm: "24px" }} 
                          lineHeight={{ base: "14px", sm: "24px" }}
                        >
                          Our Impact
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                        
                        <LinkBox
                          fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/seniors">
                            Silver Screenings
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox
                          fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="https://filmcircuit.tiff.net/">
                            TIFF Film Circuit 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox
                          fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/shareherjourney">
                            Share Her Journey 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/everystory">
                            Every Story 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/partner-festivals">
                            Partner Festivals 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                          <LinkOverlay href="/community-and-access">
                            Community Impact 
                          </LinkOverlay>
                        </LinkBox>

                      </AccordionPanel>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/accessibility-at-tiff">
                            Accessibility
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                      >
                        <Box as="span" flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                          lineHeight={{base: "14px", sm: "24px"}}
                        >
                          Visit Us
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }}
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/getting-here">
                            Getting Here
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }}
                        >
                          <LinkOverlay href="/hotel-partners">
                            Hotel Partners
                          </LinkOverlay>
                        </LinkBox>

                        {/* <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="#">
                            Where to Eat & Stay 
                          </LinkOverlay>
                        </LinkBox> */}

                        {/* <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                          <LinkOverlay href="#">
                            Partner Discounts
                          </LinkOverlay>
                        </LinkBox> */}

                      </AccordionPanel>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                        borderBottom="none !important"
                      >
                        <Box as="span" flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                          lineHeight={{base: "14px", sm: "24px"}}
                        >
                          Support TIFF
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                        
                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/support">
                            Donate 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                           <LinkOverlay href="/volunteer">
                            Volunteer
                          </LinkOverlay>
                        </LinkBox>

                      </AccordionPanel>
                    </AccordionItem>

                  </Accordion> {/* secondary accordian */}

                </AccordionPanel>
                </>
                )}
              </AccordionItem>

              {/* section 2 */}
              <AccordionItem  marginBottom={{base:'8px', sm: '30px'}}>
                {({ isExpanded }) => (
                  <>
                    <AccordionButton marginBottom="0px" outline="0 !important" 
                      border="0 !important" padding="8px 0px 0px 0px !important"
                    >
                    <Box as="span" flex="1" fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="700"
                        textAlign="left" maxWidth="fit-content !important"
                        paddingBottom={{base:"5px", sm:"9px"}} fontSize={{base: "16px", sm: "28px"}} lineHeight={{base: "16px", sm: "28px"}}
                        borderBottom={isExpanded ? {base:"4px solid #fff", sm:"8px solid #fff"} : "none"}
                      >
                        Festival
                      </Box>
                      <AccordionIcon width={{base: "28px", sm: "50px"}}
                        height={{base: "28px", sm: "50px"}}
                        marginBottom="8px"
                      />
                    </AccordionButton>
                  <AccordionPanel padding="0px !important">
                    <Accordion variant="secondary" allowToggle>

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/about-the-festival">
                          About TIFF 50
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>
                      
                      {showTiffMarketLink && 
                        <AccordionItem borderTop="none">
                          <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            textAlign="left"
                            lineHeight={{base: "12px", sm: "24px"}}
                            borderBottom="1px solid #fff !important"
                            marginBottom="-2px !important"
                            padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                          >
                            <LinkOverlay href="/themarket">
                              About TIFF: The Market 
                            </LinkOverlay>
                          </LinkBox>
                        </AccordionItem>
                      }

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/submissions">
                            Submissions
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>

                      {/* secondary accordian item */}
                      {/* <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/films">
                            Films & Events List
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem> */}

                      {/* secondary accordian item */}
                      {/* <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/films?schedule">
                            Schedule
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem> */}

                      {/* secondary accordian item */}
                      {/* <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/vote">
                          People's Choice Award
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem> */}

                      {/* secondary accordian item */}
                      {/* <AccordionItem borderTop="none">

                        <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                          padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                        >
                          <Box as="span" flex="1" 
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                            lineHeight={{base: "14px", sm: "24px"}}
                          >
                            Plan Your Visit
                          </Box>
                          <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                        </AccordionButton>

                        <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                          
                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/visit?tab=Venue#">
                              Venues
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/getting-here">
                              Getting Here
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/hotel-partners">
                              Hotel Partners
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/about-tiff-23?tab=tickets#">
                              Ticket Information
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }}
                          >
                            <LinkOverlay href="/accessibility-at-tiff">
                              Accessibility 
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="#">
                              Where to Eat & Stay 
                            </LinkOverlay>
                          </LinkBox> 
                          
                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                          >
                            <LinkOverlay href="#">
                              Partner Discounts
                            </LinkOverlay>
                          </LinkBox>

                        </AccordionPanel>
                      </AccordionItem> */}

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/programmers">
                            Meet the Programmers
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">

                        <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                          padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                        >
                          <Box as="span" flex="1" 
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                            lineHeight={{base: "14px", sm: "24px"}}
                          >
                            Press & Industry 
                          </Box>
                          <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                        </AccordionButton>

                        <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                          
                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/industry-fest2023?tab=conference">
                              Industry Conference
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/industry-fest2023">
                              Industry Accreditation & Resources 
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                            borderBottom='1px solid'
                          >
                            <LinkOverlay href="/press">
                              Press Accreditation & Resources 
                            </LinkOverlay>
                          </LinkBox>

                          <LinkBox fontSize={{base:'14px', sm:'22px'}}
                            fontFamily="Libre Franklin, sans-serif" 
                            fontWeight="600"
                            lineHeight={{base:'14px', sm:'22px'}}
                            padding={{base:'12px 0', sm: '20px 0' }} 
                          >
                            <LinkOverlay href="/industry-promote-screen-advertise">
                              Promote, Screen, Advertise
                            </LinkOverlay>
                          </LinkBox>

                        </AccordionPanel>
                      </AccordionItem>

                      {/* secondary accordian item */}
                      {/* <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/about-tiff-22?tab=awards#">
                            Juried Awards
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem> */}

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          borderBottom="1px solid #fff !important"
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/tribute-awards">
                            TIFF Tribute Awards
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>

                      {/* secondary accordian item */}
                      <AccordionItem borderTop="none">
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="/awards">
                            Awards
                          </LinkOverlay>
                        </LinkBox>
                      </AccordionItem>

                    </Accordion> {/* secondary accordian */}
                  </AccordionPanel>
                  </>
                )}
              </AccordionItem>

              {/* section 3 */}
              <AccordionItem  marginBottom={{base:'8px', sm: '30px'}}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton marginBottom="0px" outline="0 !important" 
                    border="0 !important" padding="8px 0px 0px 0px !important"
                  >
                   <Box as="span" flex="1" fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700"
                      textAlign="left" maxWidth="fit-content !important"
                      paddingBottom={{base:"5px", sm:"9px"}} fontSize={{base: "16px", sm: "28px"}} lineHeight={{base: "16px", sm: "28px"}}
                      borderBottom={isExpanded ? {base:"4px solid #fff", sm:"8px solid #fff"} : "none"}
                    >
                      What's On
                    </Box>
                    <AccordionIcon width={{base: "28px", sm: "50px"}}
                      height={{base: "28px", sm: "50px"}}
                      marginBottom="8px"
                    />
                  </AccordionButton>
                <AccordionPanel padding="0px !important">

                  <Accordion variant="secondary" allowToggle>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        borderBottom="1px solid #fff !important"
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/calendar">
                          Now Playing
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important', marginBottom: '-1px !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                      >
                        <Box as="span" flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                          lineHeight={{base: "14px", sm: "24px"}}
                        >
                          Programming
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                        
                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/calendar?newreleases">
                            New Releases
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/cinematheque">
                            TIFF Cinematheque
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/nextwave">
                            TIFF Next Wave 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/smc">
                            Secret Movie Club
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/icc">
                            International Cinema Cafe
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/canadas-top-ten">
                            Canada's Top Ten 
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                          <LinkOverlay href="/industry-yearround">
                            Industry Year-Round
                          </LinkOverlay>
                        </LinkBox>

                      </AccordionPanel>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        borderBottom="1px solid #fff !important"
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/varda">
                          Varda Café-Bar
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">

                      <AccordionButton _expanded={{bg: 'white !important', color: 'black !important'}}
                        padding={{base: "12px 0px 12px 8px !important", sm: "24px 0px 24px 14px !important" }}
                      >
                        <Box as="span" flex="1" 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left" fontSize={{base: "14px", sm: "24px"}} 
                          lineHeight={{base: "14px", sm: "24px"}}
                        >
                          The Library
                        </Box>
                        <AccordionIcon width={{base: "20px", sm: "40px"}} height={{base: "20px", sm: "40px"}}/>
                      </AccordionButton>

                      <AccordionPanel padding={{base:"0px 33px 19px 18px", sm:"0px 67px 24px 36px"}} >
                        
                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                          borderBottom='1px solid'
                        >
                          <LinkOverlay href="/library">
                            Visit
                          </LinkOverlay>
                        </LinkBox>

                        <LinkBox fontSize={{base:'14px', sm:'22px'}}
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          lineHeight={{base:'14px', sm:'22px'}}
                          padding={{base:'12px 0', sm: '20px 0' }} 
                        >
                          <LinkOverlay href="http://collection.tiff.net/mwebcgi/mweb?request=advform">
                            Online
                          </LinkOverlay>
                        </LinkBox>

                      </AccordionPanel>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/events/">
                          Book an Event 
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                  </Accordion> {/* secondary accordian */}

                </AccordionPanel>
                </>
                )}
              </AccordionItem>

              {/* section 4 */}
              <AccordionItem  marginBottom={{base:'8px', sm: '30px'}}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton marginBottom="0px" outline="0 !important" 
                    border="0 !important" padding="8px 0px 0px 0px !important"
                  >
                   <Box as="span" flex="1" fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700"
                      textAlign="left" maxWidth="fit-content !important"
                      paddingBottom={{base:"5px", sm:"9px"}} fontSize={{base: "16px", sm: "28px"}} lineHeight={{base: "16px", sm: "28px"}}
                      borderBottom={isExpanded ? {base:"4px solid #fff", sm:"8px solid #fff"} : "none"}
                    >
                      Talent Development
                    </Box>
                    <AccordionIcon width={{base: "28px", sm: "50px"}}
                      height={{base: "28px", sm: "50px"}}
                      marginBottom="8px"
                    />
                  </AccordionButton>
                <AccordionPanel padding="0px !important">

                  <Accordion variant="secondary" allowToggle>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        borderBottom="1px solid #fff !important"
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/industry-talent-development">
                          Talent Programmes
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/industry-alumni">
                          Talent Alumni
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                  </Accordion> {/* secondary accordian */}

                </AccordionPanel>
                </>
                )}
              </AccordionItem>

              {/* section 5 */}
              <AccordionItem>
                  <LinkBox
                    padding={{base: "10px 0px 16px 2px", sm: "16px 0px 46px 2px"}}
                    marginBottom="0px" outline="0 !important" border="0 !important"
                  >
                    <LinkOverlay href="/membership"
                      fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700"
                      textAlign="left"
                      paddingBottom="9px" fontSize={{base: "16px", sm: "28px"}} 
                      lineHeight={{base: "16px", sm: "28px"}}
                    >
                      Become a Member
                    </LinkOverlay>
                  </LinkBox>

              </AccordionItem>

              {/* section 6 */}
              <AccordionItem>
                  <LinkBox
                    padding={{base: "10px 0px 16px 2px", sm: "16px 0px 46px 2px"}}
                    marginBottom="0px" outline="0 !important" border="0 !important"
                  >
                    <LinkOverlay href="https://shop.tiff.net/"
                      fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700"
                      textAlign="left"
                      paddingBottom="9px" fontSize={{base: "16px", sm: "28px"}} 
                      lineHeight={{base: "16px", sm: "28px"}}
                    >
                      TIFF Shop
                    </LinkOverlay>
                  </LinkBox>

              </AccordionItem>

              {/* section 7 */}
              <AccordionItem  marginBottom={{base:'8px', sm: '30px'}}>
              {({ isExpanded }) => (
                <>
                  <AccordionButton marginBottom="0px" outline="0 !important" 
                    border="0 !important" padding="8px 0px 0px 0px !important"
                  >
                   <Box as="span" flex="1" fontFamily="Libre Franklin, sans-serif" 
                      fontWeight="700"
                      textAlign="left" maxWidth="fit-content !important"
                      paddingBottom={{base:"5px", sm:"9px"}} fontSize={{base: "16px", sm: "28px"}} lineHeight={{base: "16px", sm: "28px"}}
                      borderBottom={isExpanded ? {base:"4px solid #fff", sm:"8px solid #fff"} : "none"}
                    >
                      Tickets
                    </Box>
                    <AccordionIcon width={{base: "28px", sm: "50px"}}
                      height={{base: "28px", sm: "50px"}}
                      marginBottom="8px"
                    />
                  </AccordionButton>
                <AccordionPanel padding="0px !important">

                  <Accordion variant="secondary" allowToggle>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                        fontFamily="Libre Franklin, sans-serif" 
                        fontWeight="600"
                        textAlign="left"
                        lineHeight={{base: "12px", sm: "24px"}}
                        borderBottom="1px solid #fff !important"
                        marginBottom="-2px !important"
                        padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                      >
                        <LinkOverlay href="/ticket-pricing">
                          Ticket Pricing
                        </LinkOverlay>
                      </LinkBox>
                    </AccordionItem>

                    {/* secondary accordian item */}
                    <AccordionItem borderTop="none">
                      {showTicketModal ? 
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                          onClick={() => { $('#festival-buy-tickets-modal').modal(); onMobilePanelClose(); }}
                        >
                          <LinkOverlay href="#" >
                            Get Tickets
                          </LinkOverlay>
                        </LinkBox>
                      :
                        <LinkBox fontSize={{base: "14px", sm: "24px"}} 
                          fontFamily="Libre Franklin, sans-serif" 
                          fontWeight="600"
                          textAlign="left"
                          lineHeight={{base: "12px", sm: "24px"}}
                          marginBottom="-2px !important"
                          padding={{base: "16px 0px 16px 8px", sm: "30px 0px 30px 14px"}}
                        >
                          <LinkOverlay href="https://am.ticketmaster.com/tiff/">
                            <Flex alignItems="end">
                              <Box>Get Tickets</Box>
                              {isLargerThan576 ? (
                                <span className="material-symbols-outlined" style={{ fontSize: "23px", marginLeft: "6px" }}>open_in_new</span>
                              ) : (
                                <span className="material-symbols-outlined" style={{ fontSize: "small", marginLeft: "4px" }}>open_in_new</span>
                              )}
                            </Flex>
                          </LinkOverlay>
                        </LinkBox>
                      }
                    </AccordionItem>

                  </Accordion> {/* secondary accordian */}

                </AccordionPanel>
                </>
                )}
              </AccordionItem>

            </Accordion>

          </DrawerBody>

        </DrawerContent>
      </Drawer>
    </>
  );
}

export default MobilePanel;
